/**
 * Camera Action Utilities
 *
 * Platform-independent utilities for managing camera actions (photos, panoramas) at waypoints.
 *
 * @packageDocumentation
 */

import { v4 as uuidv4 } from "uuid";

import {
  PanoramaMode_Enum,
  TakePanoramaActionArgs,
} from "@skydio/pbtypes/pbtypes/vehicle/skills/actions/take_panorama/take_panorama";
import { TakePhotoActionArgs } from "@skydio/pbtypes/pbtypes/vehicle/skills/actions/take_photo/take_photo";

import { findActions, getWaypointActions, isActionType, isWaypoint } from "../actionTree";

import type { Action, Mission } from "@skydio/pbtypes/pbtypes/vehicle/skills/mission";
import type { Waypoint } from "../actionTree";

/**
 * Camera action type - either photo, panorama mode, or none
 */
export type CameraActionType =
  | "none"
  | "takePhoto"
  | PanoramaMode_Enum.HORIZONTAL
  | PanoramaMode_Enum.VERTICAL
  | PanoramaMode_Enum.SPHERE;

/**
 * Configuration for camera action
 */
export interface CameraActionConfig {
  /** Type of camera action */
  type: CameraActionType;
  /** Photo settings (if type is takePhoto) */
  photoSettings?: Partial<TakePhotoActionArgs>;
  /** Panorama settings (if type is panorama) */
  panoramaSettings?: Partial<TakePanoramaActionArgs>;
}

/**
 * Default photo action arguments
 */
const DEFAULT_PHOTO_ARGS: TakePhotoActionArgs = TakePhotoActionArgs.fromPartial({
  photoType: { eoPhoto: true, irPhoto: true },
  photoSettings: { photoType: { eoPhoto: true, irPhoto: true } },
  waitForFocusToConverge: true,
});

/**
 * Create a TakePhoto action
 *
 * @param settings - Optional photo settings
 * @param actionUuid - Optional UUID for the action (generates new if not provided)
 * @returns TakePhoto action
 */
export function createTakePhotoAction(
  settings?: Partial<TakePhotoActionArgs>,
  actionUuid?: string
): Action {
  return {
    actionKey: "TakePhoto",
    actionUuid: actionUuid ?? uuidv4(),
    args: {
      takePhoto: {
        ...DEFAULT_PHOTO_ARGS,
        ...settings,
      },
      photoOnCompletion: false, // Deprecated field, but required by ActionArgs
      isSkippable: true, // Camera actions are skippable by default
    },
  };
}

/**
 * Create a TakePanorama action
 *
 * @param mode - Panorama mode (HORIZONTAL, VERTICAL, or SPHERE)
 * @param settings - Optional panorama settings
 * @param actionUuid - Optional UUID for the action (generates new if not provided)
 * @returns TakePanorama action
 */
export function createTakePanoramaAction(
  mode: PanoramaMode_Enum,
  settings?: Partial<TakePanoramaActionArgs>,
  actionUuid?: string
): Action {
  return {
    actionKey: "TakePanorama",
    actionUuid: actionUuid ?? uuidv4(),
    args: {
      takePanorama: TakePanoramaActionArgs.fromPartial({
        panoramaMode: mode,
        ...settings,
      }),
      photoOnCompletion: false, // Deprecated field, but required by ActionArgs
      isSkippable: true, // Camera actions are skippable by default
    },
  };
}

/**
 * Get the camera action from a waypoint (if it exists)
 *
 * @param waypoint - The waypoint action
 * @returns Camera action configuration, or null if no camera action exists
 */
export function getCameraActionFromWaypoint(waypoint: Waypoint): CameraActionConfig | null {
  const actions = getWaypointActions(waypoint);

  if (actions.photo) {
    return {
      type: "takePhoto",
      photoSettings: actions.photo.args.takePhoto,
    };
  }

  if (actions.pano) {
    const panoramaMode = actions.pano.args.takePanorama?.panoramaMode;
    if (
      panoramaMode === PanoramaMode_Enum.HORIZONTAL ||
      panoramaMode === PanoramaMode_Enum.VERTICAL ||
      panoramaMode === PanoramaMode_Enum.SPHERE
    ) {
      return {
        type: panoramaMode,
        panoramaSettings: actions.pano.args.takePanorama,
      };
    }
  }

  return null;
}

/**
 * Check if waypoint has a camera action
 *
 * @param waypoint - The waypoint action
 * @returns True if waypoint has photo or panorama action
 */
export function waypointHasCameraAction(waypoint: Waypoint): boolean {
  const actions = getWaypointActions(waypoint);
  return !!actions.photo || !!actions.pano;
}

/**
 * Add or update camera action to a waypoint
 *
 * @param mission - The mission to modify
 * @param waypointIndex - Index of the waypoint (0-based)
 * @param cameraAction - Camera action configuration
 * @returns Updated mission
 */
export function setCameraActionAtWaypoint(
  mission: Mission,
  waypointIndex: number,
  cameraAction: CameraActionConfig
): Mission {
  const waypoints = findActions(mission, isWaypoint);

  if (waypointIndex < 0 || waypointIndex >= waypoints.length) {
    throw new Error(`Waypoint index ${waypointIndex} out of range (0-${waypoints.length - 1})`);
  }

  const waypointResult = waypoints[waypointIndex];
  const waypoint = waypointResult.action as Waypoint;
  const waypointActions = getWaypointActions(waypoint);

  // Find existing camera action (photo or pano)
  const existingCameraAction = waypointActions.photo ?? waypointActions.pano;

  // Clone mission for immutability
  const updatedMission = { ...mission };

  // Navigate to the waypoint in the cloned mission using the path
  let currentActions = updatedMission.actions;
  for (let i = 0; i < waypointResult.path.length - 1; i++) {
    const idx = waypointResult.path[i];
    currentActions = [...currentActions];
    if (currentActions[idx].args?.sequence) {
      currentActions[idx] = {
        ...currentActions[idx],
        args: {
          ...currentActions[idx].args,
          sequence: {
            ...currentActions[idx].args!.sequence!,
            actions: [...currentActions[idx].args!.sequence!.actions],
          },
        },
      };
      currentActions = currentActions[idx].args!.sequence!.actions;
    }
  }

  // Get the waypoint and its sequence actions
  const waypointIdx = waypointResult.path[waypointResult.path.length - 1];
  const targetWaypoint = currentActions[waypointIdx];
  const sequenceActions = [...(targetWaypoint.args?.sequence?.actions || [])];

  // Find camera action index
  const cameraActionIndex = sequenceActions.findIndex(
    action => isActionType(action, "takePhoto") || isActionType(action, "takePanorama")
  );

  // If setting to "none", remove existing camera action
  if (cameraAction.type === "none") {
    if (cameraActionIndex !== -1) {
      sequenceActions.splice(cameraActionIndex, 1);
    }
  } else {
    // Create new camera action
    let newAction: Action;
    if (cameraAction.type === "takePhoto") {
      newAction = createTakePhotoAction(
        cameraAction.photoSettings,
        existingCameraAction?.actionUuid
      );
    } else {
      // Panorama modes
      newAction = createTakePanoramaAction(
        cameraAction.type,
        cameraAction.panoramaSettings,
        existingCameraAction?.actionUuid
      );
    }

    // If there's an existing camera action, replace it
    if (cameraActionIndex !== -1) {
      sequenceActions[cameraActionIndex] = newAction;
    } else {
      // Otherwise, insert before the final setObstacleAvoidance reset
      const resetIndex = sequenceActions.findIndex(
        (action, idx) => idx > 0 && isActionType(action, "setObstacleAvoidance")
      );
      if (resetIndex === -1) {
        throw new Error("Invalid waypoint structure: missing obstacle avoidance reset");
      }
      sequenceActions.splice(resetIndex, 0, newAction);
    }
  }

  // Update the waypoint with modified actions
  currentActions[waypointIdx] = {
    ...targetWaypoint,
    args: {
      sequence: {
        name: targetWaypoint.args?.sequence?.name || "",
        hideReverseUi: targetWaypoint.args?.sequence?.hideReverseUi || false,
        actions: sequenceActions,
      },
      photoOnCompletion: targetWaypoint.args?.photoOnCompletion || false,
      isSkippable: targetWaypoint.args?.isSkippable || false,
    },
  };

  return updatedMission;
}

/**
 * Remove camera action from a waypoint
 *
 * @param mission - The mission to modify
 * @param waypointIndex - Index of the waypoint (0-based)
 * @returns Updated mission
 */
export function removeCameraActionFromWaypoint(mission: Mission, waypointIndex: number): Mission {
  return setCameraActionAtWaypoint(mission, waypointIndex, { type: "none" });
}

/**
 * Get all waypoints with camera actions
 *
 * @param mission - The mission to analyze
 * @returns Array of waypoint indices that have camera actions
 */
export function getWaypointsWithCameraActions(mission: Mission): number[] {
  const waypoints = findActions(mission, isWaypoint);
  const indicesWithCamera: number[] = [];

  waypoints.forEach((waypointResult, index) => {
    const waypoint = waypointResult.action as Waypoint;
    if (waypointHasCameraAction(waypoint)) {
      indicesWithCamera.push(index);
    }
  });

  return indicesWithCamera;
}
